import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@app/core/guards/auth.guard';
import { InvoiceResolver } from '@app/core/resolvers/invoices/invoice-detail-resolver.service';
import { InvoicesResolver } from '@app/core/resolvers/invoices/invoices-resolver.service';
import { GroupsResolver } from '@app/core/resolvers/groups/groups-resolver.service';
import { AccessRequirement } from '@app/core/models/access-requirement.model';
import { ReferentialResolver } from '@app/core/resolvers/referentials/referential-resolver.service';
import { DispatcherComponent } from '@app/features/dispatcher/dispatcher.component';
import { UnauthorizedComponent } from '@app/features/unauthorized/unauthorized.component';
import { InvoiceDetailComponent } from '@app/features/invoices/detail/invoice-detail.component';
import { InvoiceListDispatchedComponent } from '@app/features/invoices/list/dispatched/invoice-list-dispatched.component';
import { InvoiceListRejectedComponent } from '@app/features/invoices/list/rejected/invoice-list-rejected.component';
import { InvoiceListVoucherValidatedComponent } from '@app/features/invoices/list/voucher-validated/invoice-list-voucher-validated.component';
import { InvoiceListVoucherWorkflowComponent } from '@app/features/invoices/list/voucher-workflow/invoice-list-voucher-workflow.component';
import { InvoiceLateComponent } from '@app/features/invoices/list/late/invoice-list-late.component';
import { InvoiceSearchComponent } from '@app/features/invoices/list/search/invoice-list-search.component';
import { InvoiceTraceabilityComponent } from '@app/features/invoices/detail/traceability/invoice-traceability.component';
import { InvoiceVoucherComponent } from '@app/features/invoices/actions/voucher/invoice-voucher.component';
import { InvoiceVoucherProcessComponent } from '@app/features/invoices/actions/voucher/invoice-voucher-process/invoice-voucher-process.component';
import { InvoicePdfViewerComponent } from '@app/features/invoices/detail/pdf-viewer/invoice-pdf-viewer.component';
import { ReferentialType } from '@app/core/models/referentials/referential-type.model';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { InvoicesLateResolver } from '@app/core/resolvers/invoices/invoices-late-resolver.service';
import { CanCancelInvoiceProcessResolver } from '@app/core/resolvers/canperform/can-cancel-invoice-process-resolver.service';
import { SessionStorageKeys } from '@app/core/models/configuration/sessionstorage-keys.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { TableColumnsConfigKey } from '@app/core/models/configuration/table-columns-config-key.model';
import { InvoiceGuard } from '@app/core/guards/invoice.guard';
import { MissingInvoiceNumberResolver } from '@app/core/resolvers/imports/imports-missing-invoice-number-resolver.service.';
import { InvoiceListPromotionWorkflowComponent } from './list/promotion-workflow/invoice-list-promotion-workflow.component';
import { MsalGuard } from '@azure/msal-angular';

const routes: Routes = [
  {
    path: '',
    component: DispatcherComponent,
  },
  {
    path: 'invoices/rejected',
    component: InvoiceListRejectedComponent,
    resolve: {
      referentials: ReferentialResolver,
      groups: GroupsResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.SupervisionMembership],
      referentialsTypes: [ReferentialType.REJECTIONFLAGS],
      groupTypes: [GroupTypeModel.SUPERVISION],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_REJECTED,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/dispatched',
    component: InvoiceListDispatchedComponent,
    resolve: {
      invoices: InvoicesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.OperationalMembership],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_DISPATCHED,
      detailType: InvoiceViewType.DISPATCHED,
      configKey: TableColumnsConfigKey.INVOICEDISPATCHED,
    },
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'invoices/control',
    component: InvoiceListDispatchedComponent,
    resolve: {
      invoices: InvoicesResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.ControlMembership],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_CONTROL,
      detailType: InvoiceViewType.CONTROL,
      configKey: TableColumnsConfigKey.INVOICECONTROL,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/waiting-voucher',
    component: InvoiceListDispatchedComponent,
    resolve: {
      invoices: InvoicesResolver,
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [
        AccessRequirement.OperationalMembership,
        AccessRequirement.ControlMembership,
        AccessRequirement.OperationContributorMembership,
      ],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_WAITING,
      detailType: InvoiceViewType.WAITING,
      configKey: TableColumnsConfigKey.WAITING,
      referentialsTypes: [ReferentialType.WAITINGFLAGS, ReferentialType.EXPENSEFAMILYTYPE, ReferentialType.EXPENSETRADETYPE],
    },
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'invoices/voucherworkflow',
    component: InvoiceListVoucherWorkflowComponent,
    resolve: {
      invoices: InvoicesResolver,
    },
    data: { requiredMembership: [AccessRequirement.ValidationMembership] },
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'invoices/promotionworkflow',
    component: InvoiceListPromotionWorkflowComponent,
    resolve: {
      invoices: InvoicesResolver,
    },
    data: { requiredMembership: [AccessRequirement.PPRValidationMembership] },
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'invoices/vouchervalidated',
    component: InvoiceListVoucherValidatedComponent,
    data: {
      requiredMembership: [AccessRequirement.NonErpMemberShip],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_VOUCHERVALIDATED,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/search',
    component: InvoiceSearchComponent,
    resolve: {
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.InvoiceRelatedGroupMembership],
      referentialsTypes: [
        ReferentialType.REJECTIONFLAGS,
        ReferentialType.RECYCLINGFLAGS,
        ReferentialType.WAITINGFLAGS,
      ],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_SEARCH,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/late',
    component: InvoiceLateComponent,
    resolve: {
      groups: InvoicesLateResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.MultiMembership],
      searchFilterKey: SessionStorageKeys.INVOICE_FILTER_LATE,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/:invoiceViewType/voucher/:id',
    component: InvoiceVoucherComponent,
    resolve: { invoice: InvoiceResolver },
    data: {
      requiredMembership: [AccessRequirement.InvoiceRelatedGroupMembership],
    },
    canActivate: [AuthGuard, InvoiceGuard],
  },
  {
    path: 'invoices/voucher/process/:id',
    component: InvoiceVoucherProcessComponent,
    resolve: {
      invoice: InvoiceResolver,
    },
    data: { requiredMembership: [AccessRequirement.OperationalMembership] },
    canActivate: [AuthGuard, InvoiceGuard],
  },
  {
    path: 'invoices/traceability/:id',
    component: InvoiceTraceabilityComponent,
    data: {
      requiredMembership: [AccessRequirement.InvoiceRelatedGroupMembership],
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/:invoiceViewType/detail/:id',
    component: InvoiceDetailComponent,
    resolve: {
      invoice: InvoiceResolver,
      canCancelProcess: CanCancelInvoiceProcessResolver,
      referentials: ReferentialResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.InvoiceRelatedGroupMembership],
      referentialsTypes: [
        ReferentialType.VAT,
        ReferentialType.EXPENSEFAMILYTYPE,
        ReferentialType.INVOICEPPRTYPE,
        ReferentialType.DEDUCTIONTYPE,
      ],
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/inject/:id',
    component: InvoiceDetailComponent,
    resolve: {
      missingInvoiceNumber: MissingInvoiceNumberResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.AnalyticalMembership],
      invoiceViewType: InvoiceViewType.INJECTION,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/manual',
    component: InvoiceDetailComponent,
    resolve: {
      referentials: ReferentialResolver,
    },
    data: {
      referentialsTypes: [
        ReferentialType.VAT,
        ReferentialType.EXPENSEFAMILYTYPE,
        ReferentialType.INVOICEPPRTYPE,
        ReferentialType.DEDUCTIONTYPE,
      ],
      invoiceViewType: InvoiceViewType.MANUAL,
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invoices/:invoiceViewType/detail-pdf/:id',
    component: InvoicePdfViewerComponent,
    resolve: {
      invoice: InvoiceResolver,
    },
    data: {
      requiredMembership: [AccessRequirement.InvoiceRelatedGroupMembership],
    },
    canActivate: [MsalGuard,AuthGuard],
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'dispatcher',
    component: DispatcherComponent,
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
  providers: [],
})
export class InvoicesRoutingModule {}
